/**
 * Service layer for streaming chat with a LibreChat agent.
 * Calls the platform-api SSE proxy which handles LibreChat communication
 * server-side, avoiding CORS and proxy buffering issues.
 */

import { useOrgStore } from '@/stores/org-store';
import type { CollectionReference } from '@/utils/collection-references';
import { getFreshToken } from '@/utils/auth-token-provider';
import * as Sentry from '@sentry/react';

interface SendMessageOptions {
  agentId: string;
  message: string;
  images?: string[]; // Base64 data URLs
  /** Data Collections the user attached; platform-api renders them into the prompt. */
  collections?: CollectionReference[];
  conversationId?: string;
  parentMessageId?: string;
  onToken: (token: string) => void;
  onThinking?: (text: string) => void;
  onToolCall: (name: string, args: string) => void;
  onToolResult?: (name: string, result: string) => void;
  onDone: (
    fullMessage: string,
    conversationId: string,
    messageId: string,
  ) => void;
  onError: (error: string) => void;
  signal?: AbortSignal;
}

export async function streamAgentChat(opts: SendMessageOptions): Promise<void> {
  const {
    agentId,
    message,
    images,
    collections,
    conversationId,
    parentMessageId,
    onToken,
    onToolCall,
    onDone,
    onError,
    signal,
  } = opts;

  // Get org ID and fresh platform token for the proxy request
  const { currentOrg } = useOrgStore.getState();
  const orgId = currentOrg?.orgid;
  const platformToken = await getFreshToken();

  if (!orgId || !platformToken) {
    onError('Not authenticated');
    return;
  }

  // Call the platform-api SSE proxy endpoint (same-origin, no CORS issues)
  const url = `/proxy/platform/chat-agents/${orgId}/${agentId}/chat`;

  const streamStartTime = Date.now();

  try {
    console.log('[chat.ts] Calling proxy:', url);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-User-Token': platformToken,
      },
      body: JSON.stringify({
        text: message,
        images: images || [],
        collections: (collections || []).map((ref) => ({
          kind: ref.kind,
          collection_id: ref.collectionId,
          ...(ref.datasetId ? { dataset_id: ref.datasetId } : {}),
        })),
        conversationId: conversationId || 'new',
        parentMessageId:
          parentMessageId || '00000000-0000-0000-0000-000000000000',
      }),
      signal,
    });

    console.log('[chat.ts] Proxy response:', response.status);

    if (!response.ok) {
      const errText = await response.text();
      onError(`Chat failed: ${response.status} ${errText.slice(0, 200)}`);
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      onError('No response body');
      return;
    }

    const decoder = new TextDecoder();
    let fullMessage = '';
    let responseConversationId = conversationId || '';
    let msgId = '';
    let buf = '';
    let receivedFinal = false;
    let errorEvent: string | null = null;
    let lastEventType = '';

    // Read timeout: if no bytes arrive for 45s, the connection is dead.
    // The server sends keepalive comments every 15s, so 45s of silence
    // means 3 missed keepalives -- the proxy dropped the connection.
    const READ_TIMEOUT_MS = 45_000;

    /**
     * Process a batch of newline-split SSE lines. Returns true if a terminal
     * event (final or error) was encountered and the caller should stop reading.
     */
    const processLines = (lines: readonly string[]): boolean => {
      for (const line of lines) {
        // SSE keepalive comments -- connection is alive, nothing to process
        if (line.startsWith(':')) continue;

        if (!line.startsWith('data: ')) continue;
        const raw = line.slice(6).trim();
        if (!raw) continue;

        let d: Record<string, unknown>;
        try {
          d = JSON.parse(raw);
        } catch {
          continue; // skip non-JSON lines (SSE comments, empty lines, etc.)
        }

        // Initial metadata with streamId
        if (d.streamId) {
          const cid = (d as { conversationId?: string }).conversationId;
          responseConversationId = cid || responseConversationId;
          lastEventType = 'streamId';
          console.log('[chat.ts] Stream started:', d.streamId);
          continue;
        }

        // Error
        if (d.error) {
          errorEvent = String(d.error);
          lastEventType = 'error';
          return true;
        }

        // Created message
        const dMsg = (
          d as {
            created?: boolean;
            message?: { messageId?: string; conversationId?: string };
          }
        ).message;
        if (d.created && dMsg?.messageId) {
          msgId = dMsg.messageId;
          responseConversationId =
            dMsg.conversationId || responseConversationId;
          lastEventType = 'created';
          continue;
        }

        // Text delta
        if (d.event === 'on_message_delta') {
          lastEventType = 'on_message_delta';
          const parts = (d as { data?: { delta?: { content?: unknown[] } } })
            .data?.delta?.content;
          if (Array.isArray(parts)) {
            for (const p of parts as Array<{ type?: string; text?: string }>) {
              if (p.type === 'text' && p.text) {
                fullMessage += p.text;
                onToken(p.text);
              }
            }
          }
        }

        // Thinking/reasoning - skip silently (don't re-render per token)
        if (d.event === 'on_reasoning_delta') {
          lastEventType = 'on_reasoning_delta';
          continue;
        }

        // Tool call - show tool name and args
        if (
          d.event === 'on_run_step' &&
          (d as { data?: { type?: string } }).data?.type === 'tool_calls'
        ) {
          lastEventType = 'on_run_step';
          const toolCalls = (
            d as { data?: { stepDetails?: { tool_calls?: unknown[] } } }
          ).data?.stepDetails?.tool_calls;
          if (Array.isArray(toolCalls)) {
            for (const tc of toolCalls as Array<{
              function?: { name?: string; arguments?: unknown };
              name?: string;
              args?: unknown;
            }>) {
              const name = tc.function?.name || tc.name || 'tool';
              const args = tc.function?.arguments || tc.args || '';
              onToolCall(
                name,
                typeof args === 'string' ? args : JSON.stringify(args),
              );
            }
          }
        }

        // Tool result - forward to callback for progress display
        if (d.event === 'on_tool_result') {
          lastEventType = 'on_tool_result';
          const tdata = (
            d as {
              data?: { name?: string; content?: unknown; result?: unknown };
            }
          ).data;
          const name = tdata?.name || (d as { name?: string }).name || 'tool';
          const content = tdata?.content ?? tdata?.result ?? '';
          let resultText = '';
          if (typeof content === 'string') {
            resultText = content;
          } else if (Array.isArray(content)) {
            resultText = (content as Array<{ type?: string; text?: string }>)
              .filter((c) => c.type === 'text' && c.text)
              .map((c) => c.text as string)
              .join('\n');
          } else if (content) {
            resultText = JSON.stringify(content);
          }
          if (opts.onToolResult) {
            opts.onToolResult(name, resultText);
          }
          continue;
        }

        // Final message — record state so the post-loop block emits the
        // single onDone call uniformly with the dropped-stream path.
        if (d.final) {
          receivedFinal = true;
          lastEventType = 'final';
          const respMsg = (
            d as {
              responseMessage?: { content?: unknown[]; messageId?: string };
            }
          ).responseMessage;
          const content = respMsg?.content;
          if (Array.isArray(content) && !fullMessage) {
            for (const c of content as Array<{
              type?: string;
              text?: string;
            }>) {
              if (c.type === 'text' && c.text) fullMessage += c.text;
            }
            if (fullMessage) onToken(fullMessage);
          }
          msgId = respMsg?.messageId || msgId;
          return true;
        }
      }
      return false;
    };

    streamLoop: while (true) {
      const readPromise = reader.read();
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error('SSE read timeout — no data for 45s')),
          READ_TIMEOUT_MS,
        ),
      );
      const { done, value } = await Promise.race([readPromise, timeoutPromise]);
      if (done) break;

      buf += decoder.decode(value, { stream: true });
      const lines = buf.split('\n');
      buf = lines.pop() || '';

      if (processLines(lines)) {
        // Terminal event encountered. Stop reading and let the post-loop
        // block dispatch onDone/onError exactly once. cancel() lets the
        // upstream connection close promptly.
        try {
          await reader.cancel();
        } catch {
          /* ignore */
        }
        break streamLoop;
      }
    }

    // Flush the decoder's internal state (handles split UTF-8 sequences) and
    // process any remaining bytes in `buf` (a final SSE line that didn't end
    // in a newline before the connection closed).
    buf += decoder.decode();
    if (buf) {
      // The trailing buffer may itself contain multiple lines if the server
      // ended the stream without a newline after a multi-event chunk.
      processLines(buf.split('\n'));
      buf = '';
    }

    console.log('[chat.ts] Stream ended:', {
      receivedFinal,
      hadError: !!errorEvent,
      fullMessageLength: fullMessage.length,
      lastEventType,
      elapsedMs: Date.now() - streamStartTime,
    });

    if (errorEvent) {
      onError(errorEvent);
      return;
    }

    if (receivedFinal) {
      onDone(fullMessage.trim(), responseConversationId, msgId);
      return;
    }

    // Stream ended without a `final` event. The agent may still be running
    // server-side and the partial text we received may not be the complete
    // response. Surface this as an error so the UI can recover, but include
    // the partial text we did get so the user has context.
    Sentry.captureMessage('SSE stream ended without final event', {
      level: 'warning',
      extra: {
        lastEventType,
        partialLength: fullMessage.length,
        elapsedMs: Date.now() - streamStartTime,
      },
    });
    const partialNote = fullMessage
      ? ' (partial response shown above may be incomplete)'
      : '';
    onError(
      `Stream ended unexpectedly without a final response${partialNote}. The operation may still be running \u2014 please check your workflows.`,
    );
  } catch (err) {
    if (signal?.aborted) return;
    console.error('[chat.ts] Error:', err);
    Sentry.captureException(err, {
      extra: { context: 'chat-stream' },
    });
    onError(err instanceof Error ? err.message : String(err));
  }
}
